import * as React from "react";
import {useCallback, useMemo} from "react";
import {Item, User} from "../types";
import {stylesheet} from "typestyle";
import {LogLevel} from "@variocube/app-api";
import {
    classNames,
    layout,
    MaterialIcon,
    PushButton,
    theme,
    typography,
    useOccupancies,
    usePlatform,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {BoxControl} from "./BoxControl";
import {useMarkItemInGoodCondition} from "../markItemInGoodCondition";
import {useLocalization} from "../../i18n";

interface InspectItemViewProps {
    user: User;
    item: Item;
    onClose: () => void;
}

export function InspectItemView({user, item, onClose}: InspectItemViewProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const occupancies = useOccupancies();
    const occupancy = useMemo(() => {
        return item ? occupancies.find(occupancy => occupancy.content.item == item.uuid) : null;
    }, [occupancies, item]);

    const send = useMarkItemInGoodCondition();

    const handleOpen = useCallback(async (boxNumber: string) => {
        await platform.openBoxLock(boxNumber);
        await platform.log(LogLevel.Trace, "Open box command", {...user, box: boxNumber});
    }, [platform, user]);

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div className={typography.title}>{item.name}</div>
                    <div className={typography.description}>{item.description}</div>
                    <div className={typography.description}>{item.foreignId}</div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent>
                <div className={classNames(layout.horizontal, styles.content)}>
                    <div className={classNames(layout.fill, theme.moreContrast, layout.paddingMedium, styles.halfWidth)}>
                        <div className={typography.title}>{t("Box")}</div>
                        {occupancy && (
                            <div>
                                <BoxControl boxNumber={occupancy.boxNumber} onBoxOpened={() => handleOpen(occupancy.boxNumber)}/>
                            </div>
                        )}
                        {!occupancy && (
                            <div>{t("ItemNotStored")}</div>
                        )}
                    </div>
                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.paddingMedium, layout.spaceBetween)}>
                <ViewFooterItem>
                    <PushButton onClick={onClose} icon={MaterialIcon.arrow_back}>
                        {t("Back")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    <PushButton onClick={() => send.submit(user.email, item.uuid, () => setTimeout(() => onClose(), 500))} primary>
                        {t("ItemConditionGood")}
                    </PushButton>
                </ViewFooterItem>
            </ViewFooter>
        </View>
    );
}

const styles = stylesheet({
    halfWidth: {
        width: "50%"
    },
    content: {
        minHeight: "100%"
    }
});
